import { extend } from 'lodash';

const processResponse = response => response.json();

const checkStatus = response => {
    if (response.status >= 200 && response.status < 300) {
        return response;
    } else {
        const error = new Error(response.statusText);
        error.response = response;
        throw error;
    }
};

/**
 * Fetch middleware! If an action has the following shape, it will trigger
 * a network/AJAX request, and dispatch the specified actions on success or
 * error, including the JSON payload or error message. Expected fetch action
 * shape:
 * {
 *     type: 'YOUR_ACTION_TYPE',
 *     meta: {
 *         fetch: {
 *             url: '/path/to/stuff',
 *
 *             // these can be any settings that are passed to `fetch` as its
 *             // second argument. if not provided, a normal GET request is
 *             // assumed.
 *             settings: {
 *                 method: 'POST',
 *                 mode: 'cors',
 *             },
 *
 *             actions: {
 *                 // optional action to dispatch on fetch success.
 *                 // this action will include a `payload` property that includes
 *                 // any data on the fetching action's `payload` object, plus
 *                 // a `response` property containing the fetch's JSON response.
 *                 // this can, optionally, be a function instead, in which
 *                 // case the function will be called on success and the
 *                 // aforementioned `payload` will be passed as its first
 *                 // argument, and the return value of the function will be
 *                 // dispatched
 *                 success: 'YOUR_SUCCESS_ACTION',
 *
 *                 // optional action to dispatch on fetch error
 *                 // this action will include a `payload` property that includes
 *                 // an `error` property containing the error message
 *                 error: 'YOUR_ERROR_ACTION',
 *             },
 *         },
 *     },
 * }
 * @param {object} store Redux store
 * @returns {void}
 */
const fetchMiddleware = store => next => action => {
    // let other things act on this action before we do anything
    next(action);

    if (action.meta && action.meta.fetch) {
        const fetchData = action.meta.fetch;

        // initiate network request
        const promise = fetch(fetchData.url, fetchData.settings)
            .then(checkStatus)
            .then(processResponse);

        if (fetchData.actions) {
            const { success, error } = fetchData.actions;

            // set up an action to be dispatched on fetch resolving
            let successAction = fn => fn;
            if (success) {
                if (typeof success === 'function') {
                    successAction = json => store.dispatch(
                        success(extend({}, action.payload, { response: json })));
                } else {
                    successAction = json => store.dispatch({
                        type: success,
                        payload: extend({}, action.payload, { response: json }),
                    });
                }
            }

            // set up an action to be dispatched on fetch rejection or error
            let errorAction = fn => fn;
            if (error) {
                if (typeof error === 'function') {
                    errorAction = json => store.dispatch(
                        error(extend({}, action.payload, { response: json })));
                } else {
                    errorAction = err => store.dispatch({
                        type: error,
                        payload: err,
                    });
                }
            }

            promise.then(successAction, errorAction).catch(errorAction);
        }
    }
};

export default fetchMiddleware;



// WEBPACK FOOTER //
// ./src/store/middleware/fetch.js